import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient } from '@angular/common/http';
import { Subject, Observable, EMPTY, throwError } from 'rxjs';
import { catchError, finalize, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { SearchResult } from '@ukam/core/searchs/search-result';
import { SearchConfig } from '@ukam/core/searchs/search.config';
import { ResultService } from '@ukam/core/services/result.service';
import { CacheService } from '@ukam/core/services/cache.service';
import { copyObject } from '@ukam/core/functions/object.functions';
import { FilterFieldParams } from '@ukam/core/filters/params/filter-field.params';

export type ChangeType = 'search' | 'filter' | 'page' | 'replace';

export abstract class SearchService<T extends object> {


  protected readonly resultService = inject(ResultService);
  protected readonly matDialog = inject(MatDialog);
  private readonly httpClient = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cacheService = inject(CacheService);

  protected config: SearchConfig;
  protected url: string;
  protected fieldId!: Extract<keyof T, string>;
  protected limit: number;
  protected offset = 0;
  protected params: Record<string, unknown> = {};


  readonly change = new Subject<ChangeType>();
  readonly searchChange = new Subject<SearchResult<T>>();
  readonly pageChange = new Subject<T[]>();
  readonly selectionEnabledChange = new Subject<boolean>();
  readonly loading = signal(false);
  readonly items = signal<T[]>([]);
  readonly results = signal<SearchResult<T>[]>([]);

  count = signal(0);
  page = signal(0);
  pages = signal(0);
  startIndex = signal(0);
  endIndex = signal(0);
  scrollId = signal<string | null>(null);
  searchText = signal('');
  filter = signal<Partial<T>>({});
  researched = signal(false);
  selectedCount = signal(0);
  selectedAll = signal(false);
  firstPage = signal(true);
  lastPage = signal(false);
  refresh = signal(false);
  selectionEnabled = signal(false);

  abstract search(params?: any): Observable<SearchResult<T>>;

  constructor(config?: SearchConfig) {
    this.cacheService.register(() => this.clear());
    this.config = config ?? {};
    this.url = this.config.url ?? '';
    this.fieldId = (this.config.fieldId ?? 'id') as Extract<keyof T, string>;
    this.limit = this.config.limit ?? 100;

    this.searchChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.change.next('search'));

    this.pageChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.change.next('page'));
  }

  // --- Configuração ---

  setRefresh(): void {
    this.refresh.set(true);
  }

  setParams(params: Record<string, unknown> | null = null): void {
    this.params = params ?? {};
  }

  setFilterFields(fields: FilterFieldParams[] | null): void {
    this.params = { ...this.params, filters: fields ?? [] };
  }

  // --- Busca ---

  protected _search(): Observable<SearchResult<T>> {
    if (this.loading()) return EMPTY;

    this.loading.set(true);
    this.refresh.set(false);
    this.offset = 0;
    this.selectedAll.set(false);
    this.selectedCount.set(0);
    this.researched.set(true);

    const requestParams = {
      ...this.params,
      offset: this.offset,
      limit: this.limit,
      scrollId: this.scrollId(),
    };

    return this._executeRequest(requestParams).pipe(
      tap(result => {
        this._setResult(result);
        this.searchChange.next(result);
      }),
      catchError(error => {
        this.resultService.showError(error);
        this._setResult(null);
        return throwError(() => error);
      }),
      finalize(() => this.loading.set(false))
    );
  }

  private _executeRequest(requestParams: any): Observable<SearchResult<T>> {
    if (this.config?.method === 'get') {
      return this.httpClient.get<SearchResult<T>>(this.url, { params: requestParams as any });
    }
    return this.httpClient.post<SearchResult<T>>(this.url, requestParams);
  }

  private _fetchNextPage(): Observable<SearchResult<T>> {
    if (this.config?.scrollActive) {
      return this.httpClient.get<SearchResult<T>>(`${this.url}/next/${this.scrollId()}`);
    }

    const requestParams = { ...this.params, offset: this.offset, limit: this.limit };

    if (this.config?.method === 'get') {
      return this.httpClient.get<SearchResult<T>>(this.url, { params: requestParams as any });
    }
    return this.httpClient.post<SearchResult<T>>(this.url, requestParams);
  }

  // --- Paginação ---

  nextPage(): Observable<void> {
    return new Observable(observer => {
      const done = () => { observer.next(); observer.complete(); };

      if (this.loading() || !this.items() || this.page() <= 0) return done();
      if (this.page() >= this.pages()) {
        this.lastPage.set(true);
        return done();
      }

      this.loading.set(true);
      this.firstPage.set(false);
      this.page.update(value => value + 1);
      this.lastPage.set(this.page() >= this.pages());
      this.offset += this.limit;

      if (this.page() <= this.results().length) {
        this.items.set(this.results()[this.page() - 1]?.items ?? []);
        this.pageChange.next(this.items());
        this._calcIndexes();
        this.loading.set(false);
        return done();
      }

      this._fetchNextPage().subscribe({
        next: result => {
          if (!result) {
            this.items.set([]);
            this.results.set([]);
          } else {
            this._selectAllItems(result.items ?? []);
            this.items.set(result.items ?? []);
            this.results().push(result);
            this.pageChange.next(this.items());
          }
          this._calcIndexes();
          this.loading.set(false);
          done();
        },
        error: error => {
          this.loading.set(false);
          this._calcIndexes();
          this.resultService.showError(error);
          observer.error(error);
        }
      });
    });
  }

  previousPage(): Observable<void> {
    return new Observable(observer => {
      const done = () => { observer.next(); observer.complete(); };

      if (this.page() <= 1 || this.loading()) return done();

      this.loading.set(true);
      this.page.update(value => value - 1);
      this.firstPage.set(this.page() <= 1);
      this.lastPage.set(this.page() >= this.pages());
      this.offset -= this.limit;

      if (this.page() > 0) {
        this.items.set(this.results()[this.page() - 1]?.items ?? []);
      }

      this.pageChange.next(this.items());
      this._calcIndexes();
      this.loading.set(false);
      done();
    });
  }

  // --- Seleção ---

  onSelected(item: T): void {
    if (!item) return;
    const selected = (item as any).selected as boolean;
    this.selectedCount.update(value => value + (selected ? 1 : -1));
  }

  selectAll(): void {
    this.selectionEnabled.set(true);
    const value = !this.selectedAll();
    this.selectedAll.set(value!);
    this.selectedCount.set(value ? this.count() : 0);

    for (const result of this.results()) {
      this._selectAllItems(result.items ?? []);
    }
  }

  toggleSelection(): void {
    this.selectionEnabled.set(!this.selectionEnabled());
    this.selectionEnabledChange.next(this.selectionEnabled());
  }

  private _selectAllItems(items: T[]): void {
    for (const item of items) {
      (item as any).selected = this.selectedAll();
    }
  }

  getSelectedIds(): string[] {
    return this.items()
      .filter(item => (item as any).selected)
      .map(item => (item as any)[this.fieldId] as string);
  }

  // --- Manipulação de itens ---

  replaceItem(item: T): void {
    if (!item) return;

    const currentItems = this.items();
    const index = currentItems.findIndex(
      el => (item as any)[this.fieldId] === (el as any)[this.fieldId]
    );

    if (index > -1) {
      currentItems[index] = copyObject(item);
    } else {
      currentItems.push(copyObject(item));
    }

    this.change.next('replace');
  }

  removeItemById(id: string): void {
    if (!id || this.page() <= 0) return;

    const index = this.items().findIndex(el => id === (el as any)[this.fieldId]);
    if (index > -1) {
      this._spliceItem(index);
    }
  }

  removeItem(item: T): void {
    if (!item || this.page() <= 0) return;

    const index = this.items().indexOf(item);
    if (index > -1) {
      this._spliceItem(index);
    }
  }

  removeSelectedItems(): void {
    const remaining = this.items().filter(item => {
      if ((item as any).selected) {
        this.count.update(value => value - 1);
        this.selectedCount.update(value => value - 1);
        return false;
      }
      return true;
    });

    this.items.set(remaining);
    if (this.page() > 0) {
      this.results()[this.page() - 1].items = remaining;
    }
  }

  removeItems(ids: string[]): void {
    if (!ids?.length) return;

    const idSet = new Set(ids);
    const remaining = this.items().filter(item => {
      if (idSet.has(item[this.fieldId] as string)) {
        this.count.update(value => value - 1);
        this.selectedCount.update(value => value - 1);
        return false;
      }
      return true;
    });

    this.items.set(remaining);
    if (this.page() > 0) {
      this.results()[this.page() - 1].items = remaining;
    }
  }

  private _spliceItem(index: number): void {
    const item = this.items()[index];
    this.items().splice(index, 1);
    this.count.update(value => value - 1);
    if ((item as any).selected) this.selectedCount.update(value => value - 1);
  }

  // --- Estado interno ---

  protected _setResult(result: SearchResult<T> | null): void {
    this._resetState();
    this._clearResult();

    if (!result) {
      this.lastPage.set(true);
      return;
    }

    this.items.set(result.items ?? []);
    this.count.set(result.count ?? 0);
    this.page.set(result.page ?? 0);
    this.pages.set(result.pages ?? 0);
    this.scrollId.set(result.scrollId ?? null);
    this.results().push(result);
    this.firstPage.set(true);
    this.lastPage.set(this.page() >= this.pages());
    this._calcIndexes();
  }

  protected _clearResult(): void {
    this.refresh.set(false);
    this.items.set([]);
    this.results.set([]);
    this.firstPage.set(true);
    this.lastPage.set(false);
    this._calcIndexes();
  }

  clear(): void {
    this._resetState();
    this._clearResult();
    this.researched.set(false);
    this.selectedCount.set(0);
    this.selectedAll.set(false);
    this.selectionEnabled.set(false);
    this.params = {};
    this.offset = 0;
    this.loading.set(false);
  }

  private _resetState(): void {
    this.count.set(0);
    this.page.set(0);
    this.pages.set(0);
    this.startIndex.set(0);
    this.endIndex.set(0);
  }

  private _calcIndexes(): void {
    const start = this.page() > 0 ? this.limit * (this.page() - 1) + 1 : 0;
    this.startIndex.set(start);
    this.endIndex.set(start + this.items().length - 1);
  }
}
